import React, { useState, useEffect } from 'react';
import { agencyService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Building2, ExternalLink } from 'lucide-react';
import ImageUploader from '../components/ImageUploader';

const AgencyProfilePage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agency, setAgency] = useState(null);
  const [formData, setFormData] = useState({
    publicProfile: {
      displayName: '',
      description: '',
      addressText: '',
      phonePublic: '',
      phoneSecondary: '',
      emailPublic: '',
      social: {
        instagram: '',
        facebook: '',
        website: ''
      }
    },
    media: {
      logoUrl: '',
      bannerUrl: ''
    }
  });

  const [publishInAutoflow, setPublishInAutoflow] = useState(true);

  useEffect(() => {
    loadAgency();
  }, []);

  const loadAgency = async () => {
    try {
      // Use the /my endpoint for agency users
      const data = await agencyService.getMyAgency();
      setAgency(data);
      setPublishInAutoflow(data.publish_in_autoflow !== false);
      setFormData({
        publicProfile: {
          displayName: data.publicProfile?.displayName || data.name || '',
          description: data.publicProfile?.description || '',
          addressText: data.publicProfile?.addressText || '',
          phonePublic: data.publicProfile?.phonePublic || '',
          phoneSecondary: data.publicProfile?.phoneSecondary || '',
          emailPublic: data.publicProfile?.emailPublic || '',
          social: {
            instagram: data.publicProfile?.social?.instagram || '',
            facebook: data.publicProfile?.social?.facebook || '',
            website: data.publicProfile?.social?.website || ''
          }
        },
        media: {
          logoUrl: data.media?.logoUrl || '',
          bannerUrl: data.media?.bannerUrl || ''
        }
      });
    } catch (error) {
      console.error('Error loading agency:', error);
      if (error.response?.status !== 404) {
        toast.error('Error al cargar agencia');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await agencyService.updateMyAgency(formData);
      toast.success('Perfil actualizado');
      loadAgency();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Save media URLs automatically after upload
  const handleLogoUpload = async (url) => {
    const newFormData = {
      ...formData,
      media: { ...formData.media, logoUrl: url }
    };
    setFormData(newFormData);
    
    // Auto-save the media
    try {
      await agencyService.updateMyAgency({ media: newFormData.media });
      toast.success('Logo guardado');
    } catch (error) {
      toast.error('Error al guardar logo');
    }
  };

  const handleBannerUpload = async (url) => {
    const newFormData = {
      ...formData,
      media: { ...formData.media, bannerUrl: url }
    };
    setFormData(newFormData);
    
    // Auto-save the media
    try {
      await agencyService.updateMyAgency({ media: newFormData.media });
      toast.success('Banner guardado');
    } catch (error) {
      toast.error('Error al guardar banner');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Cargando...</div>;
  }

  if (!agency) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-600 mb-2">No estás asociado a ninguna agencia</p>
          <p className="text-sm text-slate-500">
            Contacta con el administrador para que te asocie a una agencia.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900">Mi Agencia</h1>
        <p className="text-slate-600 mt-2">Edita el perfil público de tu agencia</p>
      </div>

      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">{agency.name}</h2>
              <p className="text-slate-500 mb-2">
                URL pública: <span className="font-mono text-blue-600">/a/{agency.slug}</span>
              </p>
              <div className="flex gap-2 text-sm">
                <span className={`px-2 py-1 rounded ${
                  agency.status === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {agency.status === 'active' ? 'Activa' : agency.status}
                </span>
                <span className="px-2 py-1 rounded bg-blue-100 text-blue-800">
                  Plan: {agency.plan}
                </span>
              </div>
            </div>
            <a 
              href={`/a/${agency.slug}`} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="outline" size="sm">
                <ExternalLink className="w-4 h-4 mr-2" />
                Ver sitio público
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Autos - Flow Toggle */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Publicar mis autos en Autos - Flow</h3>
              <p className="text-sm text-slate-500 mt-1">Cuando esta activado, tus vehiculos apareceran en la pagina publica Autos - Flow</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={publishInAutoflow}
              onClick={async () => {
                const newVal = !publishInAutoflow;
                setPublishInAutoflow(newVal);
                try {
                  await agencyService.updateMyAgency({ publish_in_autoflow: newVal });
                  toast.success(newVal ? 'Publicacion en Autos - Flow activada' : 'Publicacion en Autos - Flow desactivada');
                } catch (err) {
                  setPublishInAutoflow(!newVal);
                  toast.error('Error al actualizar');
                }
              }}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 flex-shrink-0 ${publishInAutoflow ? 'bg-green-500' : 'bg-slate-300'}`}
              data-testid="autoflow-toggle"
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${publishInAutoflow ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit}>
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Imágenes de la Agencia</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ImageUploader
                agencyId={agency.id}
                uploadType="agency_logo"
                currentImageUrl={formData.media.logoUrl}
                onUploadComplete={handleLogoUpload}
                aspectRatio="1/1"
                label="Logo de la agencia"
              />
              <ImageUploader
                agencyId={agency.id}
                uploadType="agency_banner"
                currentImageUrl={formData.media.bannerUrl}
                onUploadComplete={handleBannerUpload}
                aspectRatio="16/6"
                label="Banner de la agencia"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Información Pública</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Nombre a mostrar</label>
              <Input
                value={formData.publicProfile.displayName}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  publicProfile: { ...formData.publicProfile, displayName: e.target.value }
                })}
                placeholder="Mi Agencia de Autos"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Descripción</label>
              <Textarea
                value={formData.publicProfile.description}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  publicProfile: { ...formData.publicProfile, description: e.target.value }
                })}
                placeholder="Describe tu agencia para los visitantes..."
                rows={4}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Dirección</label>
              <Input
                value={formData.publicProfile.addressText}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  publicProfile: { ...formData.publicProfile, addressText: e.target.value }
                })}
                placeholder="Av. del Libertador 1234, Buenos Aires"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Datos de Contacto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Teléfono Público</label>
                <Input
                  value={formData.publicProfile.phonePublic}
                  onChange={(e) => setFormData({
                    ...formData,
                    publicProfile: { ...formData.publicProfile, phonePublic: e.target.value }
                  })}
                  placeholder="+54 11 1234-5678"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Email Público</label>
                <Input
                  type="email"
                  value={formData.publicProfile.emailPublic}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    publicProfile: { ...formData.publicProfile, emailPublic: e.target.value }
                  })}
                  placeholder="ventas@agencia.com"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Teléfono Secundario (opcional)</label>
                <Input
                  value={formData.publicProfile.phoneSecondary}
                  onChange={(e) => setFormData({
                    ...formData,
                    publicProfile: { ...formData.publicProfile, phoneSecondary: e.target.value }
                  })}
                  placeholder="+54 11 9876-5432"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Redes Sociales</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Instagram</label>
                <Input
                  value={formData.publicProfile.social.instagram}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    publicProfile: { 
                      ...formData.publicProfile, 
                      social: { ...formData.publicProfile.social, instagram: e.target.value }
                    }
                  })}
                  placeholder="https://instagram.com/agencia"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Facebook</label>
                <Input
                  value={formData.publicProfile.social.facebook}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    publicProfile: { 
                      ...formData.publicProfile, 
                      social: { ...formData.publicProfile.social, facebook: e.target.value }
                    }
                  })}
                  placeholder="https://facebook.com/agencia"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Website</label>
                <Input
                  value={formData.publicProfile.social.website}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    publicProfile: { 
                      ...formData.publicProfile, 
                      social: { ...formData.publicProfile.social, website: e.target.value }
                    }
                  })}
                  placeholder="https://www.agencia.com"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button type="submit" disabled={saving} data-testid="save-button">
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AgencyProfilePage;
